import { differenceInDays } from 'date-fns';
import type { CSSProperties } from 'react';
import { FormattedDate, FormattedMessage, FormattedTime } from 'react-intl';
import styled from 'styled-components';

import { Cards } from '#src/assets/icons/Cards.tsx';
import { ScoreTarget } from '#src/atoms/icons/ScoreTarget.tsx';
import { Shards } from '#src/atoms/icons/Shards.tsx';
import { Wheel } from '#src/atoms/icons/Wheel.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconClock } from '#src/atoms/icons/system/iconClock.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyS,
  BrandT,
  BrandXS,
  LabelM,
} from '#src/atoms/typography/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { TimeLeft } from '#src/contexts/ticker/TimeLeft/index.tsx';
import {
  useCommonGameSet,
  useCurrentSetBackground,
  useSetLogoImage,
} from '#src/set/index.ts';

import { TimeLeftBox } from './TimeLeftBox';

const StyledDialog = styled(Dialog)`
  background:
    linear-gradient(
      to bottom,
      transparent 0%,
      var(--c-black) calc(40 * var(--unit))
    ),
    var(--background);
`;
const Content = styled(Vertical).attrs({ gap: 3, center: true })`
  padding: 0 var(--triple-unit) var(--double-unit);
`;
const MainTextBlock = styled(Vertical).attrs({ gap: 2 })`
  align-items: flex-start;
  align-self: flex-start;
`;
const ItemWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  align-items: flex-start;
  color: var(--c-nd-600);
`;

const items = ({ setName, endDate }: { setName: string; endDate: Date }) => [
  {
    key: '1',
    message: (
      <FormattedMessage
        defaultMessage="{setName} Tickets expire — use them before they're gone"
        values={{ setName }}
      />
    ),
    icon: <Wheel size={2.5} />,
  },
  {
    key: '2',
    message: (
      <FormattedMessage
        defaultMessage="{setName} Essence converts to Legacy Essence"
        values={{ setName }}
      />
    ),
    icon: <Shards size={2} />,
  },
  {
    key: '3',
    message: (
      <FormattedMessage
        defaultMessage="{setName} cards converts to Legacy gameplay"
        values={{ setName }}
      />
    ),
    icon: <Cards size={2} />,
  },
  {
    key: '4',
    message: (
      <FormattedMessage
        defaultMessage="{setName} Streaks are still eligible through your last game {dayOfTheWeek}"
        values={{
          setName,
          dayOfTheWeek: <FormattedDate value={endDate} weekday="long" />,
        }}
      />
    ),
    icon: <ScoreTarget />,
  },
];

type Props = { open: boolean; onClose: () => void; endDate: Date };
export const SetWillEndDialog = ({ open, onClose, endDate }: Props) => {
  const { background } = useCurrentSetBackground();
  const commonGameSet = useCommonGameSet();
  const displayName = commonGameSet.name;

  const lessThan1Day = differenceInDays(endDate, new Date()) <= 1;

  const color = lessThan1Day ? 'var(--c-red-400)' : 'var(--c-yellow-400)';
  const daysLeft = differenceInDays(endDate, new Date());
  const logoImage = useSetLogoImage()(undefined);

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      style={{ '--background': background } as CSSProperties}
      fullWidth
      maxWidth="xs"
    >
      <Content>
        <Vertical gap={2} center>
          <img src={logoImage ?? undefined} alt="" height={48} />
          <TimeLeftBox style={{ '--shadow-color': color } as CSSProperties}>
            <Horizontal gap={0.5} center>
              <FontAwesomeIcon color={color} icon={iconClock} size="xs" />
              <BrandXS color={color}>
                {lessThan1Day ? (
                  <TimeLeft time={endDate} />
                ) : (
                  <FormattedMessage
                    defaultMessage="{nbDays, plural, one {# day left} other {# days left}}"
                    values={{ nbDays: daysLeft }}
                  />
                )}
              </BrandXS>
            </Horizontal>
          </TimeLeftBox>
        </Vertical>
        <MainTextBlock>
          <Vertical gap={0.5}>
            <BrandT>
              {lessThan1Day ? (
                <FormattedMessage
                  defaultMessage="{setName} set Ends in less than <bold>24 hours.</bold>"
                  values={{
                    bold: chunks => (
                      <BrandT color={color} as="span" bold>
                        {chunks}
                      </BrandT>
                    ),
                    setName: displayName,
                  }}
                />
              ) : (
                <FormattedMessage
                  defaultMessage="{setName} set Ends this {day}."
                  values={{
                    day: <FormattedDate value={endDate} weekday="long" />,
                    setName: displayName,
                  }}
                />
              )}
            </BrandT>
            <BodyS>
              <FormattedMessage defaultMessage="Here's what to know to make the most of your last week." />
            </BodyS>
          </Vertical>
          <Vertical>
            <LabelM bold>
              <FormattedMessage
                defaultMessage="On {day} at {time}:"
                values={{
                  day: <FormattedDate value={endDate} weekday="long" />,
                  time: (
                    <FormattedTime
                      value={endDate}
                      hour="2-digit"
                      minute="2-digit"
                    />
                  ),
                }}
              />
            </LabelM>
            {items({ setName: displayName || '', endDate }).map(
              ({ message, key, icon }) => (
                <ItemWrapper key={key}>
                  {icon}
                  <LabelM color="var(--c-nd-600)">{message}</LabelM>
                </ItemWrapper>
              )
            )}
          </Vertical>
        </MainTextBlock>
      </Content>
    </StyledDialog>
  );
};
